/**
 * RCA State Context
 * Project Risk & Complexity Assessment
 *
 * React Context with reducer for application state management.
 * Persists to localStorage under 'rca_current' (same key as vanilla app
 * for backward compatibility). Validates loaded state with Zod.
 *
 * @version 2.0.0
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  type Dispatch,
} from 'react';
import { z } from 'zod';
import type {
  RCAState,
  RCAAction,
  PhaseId,
  WizardStep,
  SectionType,
  AssessmentStatus,
  AnswerValue,
} from '../types/rca';

// ============================================================
// Constants
// ============================================================

const STORAGE_KEY = 'rca_current';
const VALID_PHASE_IDS: PhaseId[] = ['pre_charter', 'initiation', 'planning', 'event_driven'];
const ANSWER_KEY_PATTERN = /^[a-z_]+_(risk|complexity)_\d+[a-c]?$/;

// ============================================================
// Zod Schema for localStorage validation
// ============================================================

const WizardStepSchema = z.enum(['welcome', 'phases', 'assessment', 'results', 'dashboard']);
const PhaseIdSchema = z.enum(['pre_charter', 'initiation', 'planning', 'event_driven']);
const SectionTypeSchema = z.enum(['risk', 'complexity']);
const StatusSchema = z.enum(['draft', 'final']);

const AnswerValueSchema = z.union([
  z.literal('NA'),
  z.number().int().min(0).max(9),
]);

const AnswersSchema = z.record(
  z.string().regex(ANSWER_KEY_PATTERN),
  AnswerValueSchema
);

const RCAStateSchema = z.object({
  step: WizardStepSchema.default('welcome'),
  department: z.string().default(''),
  departmentCustom: z.string().default(''),
  project: z.string().default(''),
  assessor: z.string().default(''),
  assessorEmail: z.string().default(''),
  activePhases: z.array(PhaseIdSchema).default([]),
  currentPhaseIdx: z.number().int().min(0).default(0),
  currentSection: SectionTypeSchema.default('risk'),
  currentQuestionIdx: z.number().int().min(0).default(0),
  answers: AnswersSchema.default({}),
  savedAt: z.string().nullable().default(null),
  status: StatusSchema.default('draft'),
  assessmentId: z.string().default(''),
  spItemId: z.number().int().nullable().default(null),
}).partial();

// ============================================================
// Assessment ID generation
// ============================================================

function generateAssessmentId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = 'RCA-';
  for (let i = 0; i < 5; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
}

// ============================================================
// Initial State
// ============================================================

const initialState: RCAState = {
  step: 'welcome',
  department: '',
  departmentCustom: '',
  project: '',
  assessor: '',
  assessorEmail: '',
  activePhases: ['pre_charter', 'initiation', 'planning', 'event_driven'],
  currentPhaseIdx: 0,
  currentSection: 'risk',
  currentQuestionIdx: 0,
  answers: {},
  savedAt: null,
  status: 'draft',
  assessmentId: '',
  spItemId: null,
};

// ============================================================
// Reducer
// ============================================================

function rcaReducer(state: RCAState, action: RCAAction): RCAState {
  switch (action.type) {
    case 'SET_ANSWER': {
      if (!ANSWER_KEY_PATTERN.test(action.key)) {
        console.warn(`RCAContext: Rejected invalid answer key: ${action.key}`);
        return state;
      }
      const val = action.value;
      const isValidVal =
        val === 'NA' ||
        (typeof val === 'number' && Number.isInteger(val) && val >= 0 && val <= 9);
      if (!isValidVal) {
        console.warn(`RCAContext: Rejected invalid answer value: ${String(val)}`);
        return state;
      }
      return {
        ...state,
        answers: { ...state.answers, [action.key]: action.value },
      };
    }

    case 'SET_STEP':
      return { ...state, step: action.step };

    case 'SET_PHASE':
      return { ...state, currentPhaseIdx: Math.max(0, action.phaseIdx) };

    case 'SET_QUESTION':
      return { ...state, currentQuestionIdx: Math.max(0, action.questionIdx) };

    case 'SET_SECTION':
      return { ...state, currentSection: action.section };

    case 'SET_PROJECT_META':
      return { ...state, ...action.payload };

    case 'RESET':
      return {
        ...initialState,
        assessmentId: generateAssessmentId(),
        activePhases: ['pre_charter', 'initiation', 'planning', 'event_driven'],
      };

    case 'LOAD_STATE': {
      const merged = { ...state, ...action.payload };
      // Enforce valid activePhases after load
      merged.activePhases = (merged.activePhases ?? []).filter(
        (p): p is PhaseId => VALID_PHASE_IDS.includes(p as PhaseId)
      );
      return merged;
    }

    default:
      return state;
  }
}

// ============================================================
// localStorage persistence
// ============================================================

function saveToStorage(state: RCAState): void {
  try {
    const toSave: RCAState = { ...state, savedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (err) {
    if (err instanceof Error && err.name === 'QuotaExceededError') {
      console.warn('RCAContext: localStorage quota exceeded — state not saved.');
    }
  }
}

function loadFromStorage(): Partial<RCAState> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    const result = RCAStateSchema.safeParse(parsed);
    if (result.success) {
      return result.data as Partial<RCAState>;
    }
    console.warn('RCAContext: Stored state failed validation, discarding.', result.error.flatten());
    return null;
  } catch {
    return null;
  }
}

// ============================================================
// Context
// ============================================================

interface RCAContextValue {
  state: RCAState;
  dispatch: Dispatch<RCAAction>;
}

const RCAContext = createContext<RCAContextValue | null>(null);

// ============================================================
// Provider
// ============================================================

export function RCAProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = useReducer(rcaReducer, initialState);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = loadFromStorage();
    if (saved) {
      dispatch({ type: 'LOAD_STATE', payload: saved });
    }
  }, []);

  // Persist to localStorage on every state change
  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  return (
    <RCAContext.Provider value={{ state, dispatch }}>
      {children}
    </RCAContext.Provider>
  );
}

// ============================================================
// Hook
// ============================================================

export function useRCA(): RCAContextValue {
  const ctx = useContext(RCAContext);
  if (!ctx) {
    throw new Error('useRCA must be used within an RCAProvider');
  }
  return ctx;
}

// ============================================================
// Exported types
// ============================================================

export type RCADispatch = Dispatch<RCAAction>;

export type {
  RCAState,
  RCAAction,
  PhaseId,
  WizardStep,
  SectionType,
  AssessmentStatus,
  AnswerValue,
};
